<template>
  <v-container>
    <v-row>
      <v-col>
        <v-data-table
          must-sort
          show-expand
          :headers="headers"
          :items="orders.data"
          :loading="loading"
          :server-items-length="orders.total"
          :page.sync="page"
          :items-per-page.sync="itemsPerPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :footer-props="{
            'items-per-page-options': [5, 10, 20, 50]
          }"
        >
          <template v-slot:top>
            <v-toolbar flat dense>
              <v-btn rounded color="primary" @click="openAddDialog()">Neue Bestellung</v-btn>

              <v-spacer></v-spacer>

              <v-text-field
                v-model="search"
                v-debounce="fetchOrders"
                label="Bestellnr. oder Kunden suchen"
                single-line
                hide-details
              >
                <template v-slot:append>
                  <v-btn v-if="search" small icon @click="search = ''; fetchOrders()">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                  <v-icon v-else>mdi-magnify</v-icon>
                </template>
              </v-text-field>
            </v-toolbar>
          </template>

          <template v-slot:item.id="{ item }">
            <v-badge :value="!item.done" dot>
              <v-chip small label color="secondary">{{ item.id }}</v-chip>
            </v-badge>
          </template>

          <template v-slot:item.customer_name="{ item }">
            {{ item.customer_name }}
            <span class="caption font-italic">{{ item.town }}</span>
          </template>

          <template v-slot:item.created_at="{ value }">{{ dayjs(value) }}</template>

          <template
            v-slot:item.delivery_total="{ item }"
          >{{ $currency(item.delivery_price).format(true) | ff }}</template>

          <template v-slot:item.total="{ item }">
            <v-tooltip left color="black">
              <template v-slot:activator="{ on }">
                <span
                  v-on="on"
                >{{ $currency(item.sales_price).add(item.delivery_price).format(true) | ff }}</span>
              </template>
              enhaltene MwSt. {{ $currency(item.sales_tax).add(item.delivery_tax).format(true) | ff }}
            </v-tooltip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn icon @click="checkOrder(item.id)" v-if="!item.done" :loading="loadingCheck == item.id">
              <v-icon>mdi-check</v-icon>
            </v-btn>
            
            <v-tooltip top color="black">
              <template v-slot:activator="{ on }">
                <v-btn icon @click="openAddDialog(item)" v-on="on">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>

              Neue Bestellung mit diesen Kundendaten
            </v-tooltip>

            <v-btn icon @click="openEditDialog(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>

            <v-btn icon :loading="loadingDelete == item.id" @click="deleteOrder(item.id)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <v-container fluid>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-subheader>Kundendaten</v-subheader>
                    <p>
                      {{ item.customer_name }}
                      <br />
                      <span v-if="item.company">
                        {{ item.company }}
                        <br />
                      </span>
                      {{ item.street }}
                      <br />
                      {{ item.town }}
                      <br />
                    </p>
                    <p>
                      <span v-if="item.spread">
                        <v-icon small>mdi-hand</v-icon>
                        Rasendünger ausstreuen
                        <br />
                      </span>

                      <v-icon small>mdi-phone</v-icon>
                      {{ item.phone }}
                      <br />

                      <span v-if="item.email">
                        <v-icon small>mdi-at</v-icon>
                        {{ item.email }}
                        <br />
                      </span>

                      <span v-if="item.alternative_1">
                        {{ item.alternative_1 }}
                        <br />
                      </span>

                      <span v-if="item.alternative_2">
                        {{ item.alternative_2 }}
                        <br />
                      </span>
                    </p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-subheader>Bestellung</v-subheader>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th>Stück</th>
                          <th>Produkt</th>
                          <th class="text-right">Einzelpreis in €</th>
                          <th class="text-right">Gesamtpreis in €</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="orderProduct in item.products" :key="orderProduct.id">
                          <td align="middle">{{ orderProduct.amount }}</td>
                          <td>{{ orderProduct.product.name }} {{ orderProduct.product.weight | ff }}kg</td>
                          <td
                            align="right"
                          >{{ $currency(orderProduct.product_price).format() | ff }}</td>
                          <td
                            align="right"
                          >{{ $currency(orderProduct.product_price).multiply(orderProduct.amount).format() | ff }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-col>
                </v-row>
              </v-container>
            </td>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <EditOrder v-if="editDialog" v-model="editDialog" @edited="fetchOrders" :order="currentOrder" />
    <NewOrder v-if="dialog" ref="newOrder" v-model="dialog" @added="fetchOrders" />
  </v-container>
</template>

<script>
import dayjs from "dayjs";
import EditOrder from "@/components/EditOrder";
import NewOrder from "@/components/NewOrder";

export default {
  created() {
    this.fetchOrders();
  },
  components: {
    EditOrder,
    NewOrder
  },
  data: () => ({
    headers: [
      { text: "Bestell-Nr.", value: "id", sortable: false },
      { text: "Kundenname", value: "customer_name" },
      { text: "Bestelldatum", value: "created_at" },
      {
        text: "Versandkosten",
        value: "delivery_total",
        align: "right",
        sortable: false
      },
      {
        text: "Rechnungsbetrag",
        value: "total",
        align: "right",
        sortable: false
      },
      { text: "Aktionen", value: "actions", align: "right", sortable: false }
    ],
    orders: {},
    loading: false,
    loadingDelete: 0,
    loadingCheck: 0,
    page: 1,
    itemsPerPage: 10,
    sortBy: "created_at",
    sortDesc: true,
    dialog: false,
    editDialog: false,
    currentOrder: null,
    search: ""
  }),
  methods: {
    dayjs(date) {
      return dayjs(date).format("DD.MM.YYYY");
    },
    async openAddDialog(order) {
      this.dialog = true
      await this.$nextTick()
      this.$refs.newOrder.openWith(order)
    },
    async openEditDialog(order) {
      this.currentOrder = order
      await this.$nextTick()
      this.editDialog = true
    },
    async fetchOrders() {
      this.loading = true;

      try {
        const { data } = await this.$http.get(
          `/order/?page=${this.page}&per_page=${this.itemsPerPage}&order_by=${this.sortBy}&desc=${this.sortDesc}&search=${this.search}`
        );
        this.orders = data;
      } catch (e) {
        alert("Fehler beim Laden der Bestellungen: " + e.message);
      }

      this.loading = false;
    },
    async deleteOrder(id) {
      if (
        !confirm(
          "Sind Sie sicher, dass Sie die Bestellung löschen möchten? Dabei gehen die mit dieser Bestellung verbundenen Daten verloren."
        )
      )
        return;

      this.loadingDelete = id;

      try {
        await this.$http.delete(`/order/${id}/`);
        this.fetchOrders();
      } catch (e) {
        alert("Fehler beim Löschen der Bestellung: " + e.message);
      }

      this.loading = 0;
    },
    async checkOrder(id) {
      this.loadingCheck = id;

      try {
        await this.$http.patch(`/order/${id}/`);
        await this.fetchOrders();
      } catch (e) {
        alert("Fehler beim Fertigstellen der Bestellung: " + e.message);
      }

      this.loadingCheck = 0;
    }
  },
  watch: {
    page: "fetchOrders",
    itemsPerPage: "fetchOrders",
    sortBy: "fetchOrders",
    sortDesc: "fetchOrders"
  }
};
</script>

<style>
</style>